import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, groupBy } from 'rxjs';
import { map, switchMap, exhaustMap, mergeMap, catchError } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { mappedHttpErrorResponseOperator } from '@adnova/jf-ng-components';

import { AnnouncementAdminService, ProblemDTO } from '../../openapi/shell-service-openapi';
import { AnnouncementEntitiesActions } from './announcement-entities.actions';

/**
 * Effects für Announcement-Entities.
 *
 * Handhabt alle HTTP-Requests für Announcements.
 */
@Injectable()
export class AnnouncementEntitiesEffects {
  private readonly actions$ = inject(Actions);
  private readonly service = inject(AnnouncementAdminService);
  private readonly logger = inject(NGXLogger);

  /**
   * Lädt die Liste der Announcements.
   */
  readonly loadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnnouncementEntitiesActions.loadList),
      switchMap(({ pageable }) =>
        this.service.readAnnouncements(pageable).pipe(
          map((response) =>
            AnnouncementEntitiesActions.loadListSuccess({
              announcements: response.content || [],
              totalElements: response.totalElements ?? 0,
            }),
          ),
          catchError((error) => of(error).pipe(
            mappedHttpErrorResponseOperator<ProblemDTO>(error),
            map((mappedError) => {
              this.logger.error('Fehler beim Laden der Announcements.', mappedError);
              return AnnouncementEntitiesActions.loadListFailure({ error: mappedError });
            }),
          )),
        ),
      ),
    ),
  );

  /**
   * Lädt die Statistiken.
   */
  readonly loadStatistics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnnouncementEntitiesActions.loadStatistics),
      switchMap(() =>
        this.service.readAnnouncementStatistics().pipe(
          map((statistics) =>
            AnnouncementEntitiesActions.loadStatisticsSuccess({ statistics }),
          ),
          catchError((error) => of(error).pipe(
            mappedHttpErrorResponseOperator<ProblemDTO>(error),
            map((mappedError) => {
              this.logger.error('Fehler beim Laden der Statistiken.', mappedError);
              return AnnouncementEntitiesActions.loadStatisticsFailure({ error: mappedError });
            }),
          )),
        ),
      ),
    ),
  );

  /**
   * Löscht ein Announcement.
   *
   * Verwendet groupBy + exhaustMap: Doppelklick-Schutz pro Entity,
   * parallele Löschvorgänge verschiedener Entities möglich.
   */
  readonly delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnnouncementEntitiesActions.delete),
      groupBy(action => action.id),
      mergeMap(group$ => group$.pipe(
        exhaustMap(({ id }) =>
          this.service.deleteAnnouncement(id).pipe(
            map(() => AnnouncementEntitiesActions.deleteSuccess({ id })),
            catchError((error) => of(error).pipe(
              mappedHttpErrorResponseOperator<ProblemDTO>(error),
              map((mappedError) => {
                this.logger.error('Fehler beim Löschen des Announcements.', mappedError);
                return AnnouncementEntitiesActions.deleteFailure({ id, error: mappedError });
              }),
            )),
          ),
        ),
      )),
    ),
  );

  /**
   * Veröffentlicht ein Announcement.
   *
   * Verwendet groupBy + exhaustMap: Doppelklick-Schutz pro Entity,
   * parallele Publish-Vorgänge verschiedener Entities möglich.
   */
  readonly publish$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnnouncementEntitiesActions.publish),
      groupBy(action => action.id),
      mergeMap(group$ => group$.pipe(
        exhaustMap(({ id }) =>
          this.service.publishAnnouncement(id).pipe(
            map(() => AnnouncementEntitiesActions.publishSuccess({ id })),
            catchError((error) => of(error).pipe(
              mappedHttpErrorResponseOperator<ProblemDTO>(error),
              map((mappedError) => {
                this.logger.error('Fehler beim Veröffentlichen.', mappedError);
                return AnnouncementEntitiesActions.publishFailure({ id, error: mappedError });
              }),
            )),
          ),
        ),
      )),
    ),
  );

  /**
   * Zieht die Veröffentlichung eines Announcements zurück.
   *
   * Verwendet groupBy + exhaustMap: Doppelklick-Schutz pro Entity,
   * parallele Unpublish-Vorgänge verschiedener Entities möglich.
   */
  readonly unpublish$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnnouncementEntitiesActions.unpublish),
      groupBy(action => action.id),
      mergeMap(group$ => group$.pipe(
        exhaustMap(({ id }) =>
          this.service.unpublishAnnouncement(id).pipe(
            map(() => AnnouncementEntitiesActions.unpublishSuccess({ id })),
            catchError((error) => of(error).pipe(
              mappedHttpErrorResponseOperator<ProblemDTO>(error),
              map((mappedError) => {
                this.logger.error('Fehler beim Zurückziehen der Veröffentlichung.', mappedError);
                return AnnouncementEntitiesActions.unpublishFailure({ id, error: mappedError });
              }),
            )),
          ),
        ),
      )),
    ),
  );
}
